import fetch from 'isomorphic-unfetch';
import type { NormalizedCacheObject, TypePolicies } from '@apollo/client';
import {
  ApolloClient,
  HttpLink,
  InMemoryCache,
  from,
  split,
  ApolloLink,
} from '@apollo/client';
import { Hermes } from 'msand-apollo-cache-hermes';
import { getMainDefinition } from '@apollo/client/utilities';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { getParsedToken, getToken, removeToken } from './cookieParsers';
import eventEmitter, { WS_CONNECTION_EVENTS } from './eventEmitter';
import memoryMonitor from './memoryMonitor';

export type SeaberApolloClient = ApolloClient<NormalizedCacheObject> & {
  logout: (redirect?: boolean) => Promise<void>;
};

export let apolloClient: SeaberApolloClient | null = null;
let sessionTokenTimeoutId: NodeJS.Timeout | null = null;

// Type declaration for webpack HMR
interface WebpackModule extends NodeModule {
  hot?: {
    dispose: (callback: () => void) => void;
  };
}

// Polyfill fetch() on the server (used by apollo-client)
const isBrowser = typeof window !== 'undefined';
if (!isBrowser) {
  global.fetch = fetch;
}

const errors = [
  'invalid input syntax for integer: ""',
  'unrecognized configuration parameter "jwt.claims.user_id"',
  'unrecognized configuration parameter "jwt.claims.session_id"',
  'permission denied for relation charter_parties',
  'jwt expired',
];

const historyPolicy = {
  keyFields: ['id', 'sysPeriod'],
};
const typePolicies: TypePolicies = {
  PortCallHistory: historyPolicy,
  PortNoticeHistory: historyPolicy,
  StatementOfFactsHistory: historyPolicy,
  VoyageCharterHistory: historyPolicy,
  HandledByCoaHistory: historyPolicy,
  HandledBySpotHistory: historyPolicy,
  OrdersHistory: historyPolicy,
  VesselSpeedConsumption: {
    keyFields: ['shipId', 'id'],
  },
  OrganizationAreaBunkerGradeMapping: {
    keyFields: ['organizationId', 'vesselArea'],
  },
  OrdersRelatedOrgsArrRecord: {
    keyFields: ['orderId'],
  },
};

export const useHermes =
  isBrowser && !new URLSearchParams(document.location.search).has('no-hermes');

const reconnectObservers: Set<() => void> = new Set();
export const onReconnect = (callback: () => void) => {
  reconnectObservers.add(callback);
  return () => {
    reconnectObservers.delete(callback);
  };
};
const notifyReconnect = () => {
  for (const callback of reconnectObservers) {
    try {
      callback();
    } catch (e) {
      console.error(e);
    }
  }
};

function setSessionTimeout() {
  // in e2e tests, we are using hard-coded date so this logic can be skipped
  if (process.env.APP_ENV === 'e2e') return;

  const now = new Date().getTime();
  const parsedToken = getParsedToken();

  if (!parsedToken) return;

  const expirationTime = parsedToken.exp * 1000 - now;

  if (expirationTime <= 0) return;

  sessionTokenTimeoutId = setTimeout(() => {
    window.location.href = '/signin';
  }, expirationTime);
}

const url = process.env.GRAPHQL_URI;

const splitWS = process.env.SPLIT_WS === 'true';
const wsUrl = `${url?.replace(/^(http)/, 'ws')}/${splitWS ? 'ws/graphql' : 'graphql'}`;

const authOperations = [
  'Authenticate',
  'ResetPassword',
  'GoogleAuthenticate',
  'MicrosoftAuthenticate',
];

const WS_TIMEOUT = 25_000; // 25 seconds - connection timeout
const WS_KEEPALIVE = 30_000; // 30 seconds - keepalive interval

function create({ onMutationComplete }: { onMutationComplete?: () => void }) {
  const httpLink = new HttpLink({
    uri: `${url}/graphql`, // Server URL (must be absolute)
    credentials: 'same-origin',
  });

  let loggedOut = false;
  let activeSocket: WebSocket | undefined;
  const path = isBrowser && location.pathname;

  // WebSocket Client
  function initializeWsClient() {
    let reconnectAttempt = 0;
    let connected = 0;
    let pingTimeout: ReturnType<typeof setTimeout>;

    const cleanup = () => {
      clearTimeout(pingTimeout);
      activeSocket?.close();
      activeSocket = undefined;
    };

    return createClient({
      url: wsUrl,
      lazy: path === '/' || path === '/signin',
      retryAttempts: Infinity, // keep retrying while the browser is open
      connectionParams() {
        const token = getToken();
        if (!token) {
          cleanup();
          return {};
        }

        setSessionTimeout();

        return {
          Authorization: `Bearer ${token}`,
        };
      },
      shouldRetry: (errOrCloseEvent) => {
        // Don't retry if explicitly logged out
        if (loggedOut) return false;

        console.log({ errOrCloseEvent, reconnectAttempt });

        reconnectAttempt++;
        return true;
      },
      retryWait: (retries) => {
        return new Promise((resolve) =>
          setTimeout(resolve, Math.min(1000 * Math.pow(2, retries), 30000)),
        );
      },
      keepAlive: WS_KEEPALIVE,
      on: {
        connected: (socket: WebSocket) => {
          activeSocket = socket;
          reconnectAttempt = 0; // Reset reconnect attempts on successful connection
          if (connected > 0) {
            notifyReconnect(); // Notify observers
            eventEmitter.emit(WS_CONNECTION_EVENTS.RECONNECTED);
          }
          connected++;
        },
        closed: () => {
          clearTimeout(pingTimeout);
          if (loggedOut) return; // Don't show disconnected message if explicitly logged out
          eventEmitter.emit(WS_CONNECTION_EVENTS.DISCONNECTED);
        },
        ping: (received) => {
          clearTimeout(pingTimeout);
          if (!received) {
            pingTimeout = setTimeout(() => {
              if (activeSocket?.readyState === WebSocket.OPEN) {
                console.log('Ping timeout - closing connection');
                eventEmitter.emit(WS_CONNECTION_EVENTS.DISCONNECTED);
                activeSocket.close(4408, 'Request Timeout');
              }
            }, WS_TIMEOUT);
          }
        },
        pong: (received) => {
          if (received) {
            clearTimeout(pingTimeout);
          }
        },
        error: (error) => {
          console.error('WebSocket error:', error);
          if (error instanceof CloseEvent && error.code === 1006) {
            if (activeSocket) {
              activeSocket.close();
              cleanup();
            }
          }
        },
      },
    });
  }

  // Only create WebSocket client if user is authenticated (has a token)
  const token = getToken();
  const wsClient = token && path && url && initializeWsClient();

  const wsLink = wsClient && new GraphQLWsLink(wsClient);

  const middlewareLink = setContext((req) => {
    const token = getToken();
    const operationName = req?.operationName;
    if (!token || (operationName && authOperations.includes(operationName))) {
      return {};
    }
    return {
      headers: {
        authorization: `Bearer ${token}`,
      },
    };
  });

  async function logout(redirect = true) {
    if (isBrowser) {
      loggedOut = true;
      await clearClient();
      if (wsClient && activeSocket?.readyState === WebSocket.OPEN) {
        wsClient.terminate();
      }

      // When redirecting to signin, remove token and navigate
      if (redirect) {
        removeToken();
        window.location.href = '/signin';
      }
    }
  }

  const updateCompletedLink = new ApolloLink((operation, forward) => {
    const name = operation.operationName;
    if (
      name !== 'currencyApi' &&
      name !== 'BunkerEx' &&
      operation.query.definitions.find(
        (def) =>
          def.kind === 'OperationDefinition' && def.operation === 'mutation',
      )
    ) {
      onMutationComplete?.();
    }
    return forward(operation);
  });

  const errorLink = onError(function ({
    response,
    networkError,
    graphQLErrors,
  }) {
    const gqlErr = graphQLErrors?.at(0);
    const err =
      gqlErr &&
      errors.indexOf(gqlErr.message) !== -1 &&
      (gqlErr?.path?.at(0) !== 'currentUser' ||
        gqlErr.message !==
          'unrecognized configuration parameter "jwt.claims.session_id"');
    if (
      err ||
      (networkError &&
        'statusCode' in networkError &&
        (networkError.statusCode === 401 || networkError.statusCode === 403)) ||
      networkError?.message.includes('Socket closed')
    ) {
      try {
        void logout();
      } catch (err) {
        console.error(err);
      }

      if (response) {
        response.errors = undefined;
      }
    } else {
      console.error('Unhandled error', {
        response,
        networkError,
        graphQLErrors,
      });
    }
  });

  const inMemoryCache = new InMemoryCache({
    addTypename: true,
    typePolicies,
  });
  const hermes = new Hermes({
    addTypename: true,
    entityIdForNode(node) {
      if (!node) {
        return undefined;
      }
      const { __typename, id } = node;
      if (typeof __typename !== 'string') {
        return undefined;
      }
      if (__typename in typePolicies) {
        const policy = typePolicies[__typename];

        if ('keyFields' in policy && Array.isArray(policy.keyFields)) {
          return `${__typename}_${policy.keyFields.map((field) => node[field]).join('_')}`;
        }
      }
      if (!id) {
        return undefined;
      }

      return `${__typename}_${id}`;
    },
  });
  const cache = useHermes ? hermes : inMemoryCache;

  // The split function takes three parameters:
  //
  // * A function that's called for each operation to execute
  // * The Link to use for an operation if the function returns a "truthy" value
  // * The Link to use for an operation if the function returns a "falsy" value
  const splitLink =
    wsLink &&
    split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return (
          definition.kind === 'OperationDefinition' &&
          definition.operation === 'subscription'
        );
      },
      wsLink,
      httpLink,
    );

  const client = new ApolloClient({
    link: from([
      errorLink,
      middlewareLink,
      updateCompletedLink,
      splitLink || httpLink,
    ]),
    cache,
    ssrMode: !isBrowser, // Disables forceFetch on the server (so queries are only run once)
    connectToDevTools: isBrowser,
    assumeImmutableResults: true,
  }) as SeaberApolloClient;

  client.logout = logout;

  return client;
}

export async function clearClient() {
  // clear timer if it exists
  if (sessionTokenTimeoutId) {
    clearTimeout(sessionTokenTimeoutId);
  }
  if (apolloClient) {
    apolloClient.stop();
    await apolloClient.clearStore();
    apolloClient = null;
  }
}

export default function initApollo({
  onMutationComplete,
}: {
  onMutationComplete?: () => void;
} = {}) {
  // Make sure to create a new client for every server-side request so that data
  // isn't shared between connections (which would be bad)
  if (!isBrowser) {
    return create({ onMutationComplete });
  }

  // Reuse client on the client-side
  if (!apolloClient) {
    apolloClient = create({ onMutationComplete });
  }

  return apolloClient;
}

if (isBrowser && process.env.NODE_ENV === 'development') {
  const destroyMemoryMonitor = memoryMonitor({
    parent: document.body,
  });

  // Webpack/Next.js HMR cleanup
  const hotModule = (module as WebpackModule).hot;
  if (hotModule) {
    hotModule.dispose(destroyMemoryMonitor);
  }
}
